import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, Settings, ShoppingCart, LogOut, ChevronDown } from 'lucide-react';

const UserDropdown = () => {
  const { user, logout, isAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/splash');
    setIsOpen(false);
  };

  if (!user) return null;

  const userInitial = user.first_name?.charAt(0).toUpperCase() || 'U';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-white/70 transition-colors"
      >
        <div
          className="w-10 h-10 rounded-full bg-amber-400 text-[#14281D] flex items-center justify-center font-bold shadow-md ring-2 ring-[#6FBF73]/50"
          data-testid="user-avatar-initial"
        >
          {userInitial}
        </div>
        {/* iter114s.71 — "Hello {first_name}" + chevron flipped from
            `text-white` (invisible on the new light-green TopNav) to
            the same `text-[#14281D]` dark green that Products / Pricing
            / breadcrumb links use. */}
        <span className="text-[#14281D] font-medium hidden md:block">
          Hello {user.first_name}
        </span>
        <ChevronDown className={`w-4 h-4 text-[#14281D] transition-transform ${
          isOpen ? 'transform rotate-180' : ''
        }`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 z-50">
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="text-sm font-semibold text-gray-900">{user.first_name} {user.last_name}</p>
            <p className="text-xs text-gray-500 truncate">{user.email}</p>
            {isAdmin && (
              <span className="inline-block mt-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                Admin
              </span>
            )}
          </div>

          <Link
            to="/account-settings"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700"
          >
            <Settings className="w-5 h-5" />
            <span>Account settings</span>
          </Link>

          <Link
            to="/po-history"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700"
          >
            <ShoppingCart className="w-5 h-5" />
            <span>PO Request History</span>
          </Link>

          {isAdmin && (
            <Link
              to="/admin"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-purple-600"
            >
              <User className="w-5 h-5" />
              <span>Admin Dashboard</span>
            </Link>
          )}

          <div className="border-t border-gray-200 mt-2 pt-2">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-red-600 w-full"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;
